import * as React from 'react';
import lodash from 'lodash';
import Loader from 'src/components/common/Loader';
import GenericHoc from 'src/components/hoc/GenericHoc';
import ContainerCard from './container-card';
import GetExecutionData from 'src/dto/response/getExecution';
import { SEBLCardData, SEContainerCardData } from '../../SE.interfaces';
import { HocOptions } from 'src/components/hoc/GenericHoc.types';
import { commonStyles } from './tracking.styles';
import { StylesProps, UserData } from 'src/Library/Types';
import {
  fetchAllBLsForShipment,
  fetchAllCargosForShipment,
  fetchAllContainersForShipment,
} from 'src/api/shipmentExecution';
import SideDrawerTracking from 'src/components/pages/Container/SideDrawerTracking';
import { SHIPMENT_TYPE } from 'src/components/pages/Container/ContainerUtils';
import message from 'antd/lib/message';
import { blTrackingRefresh, containerTrackingRefresh, containerTrackingReset, disableBl, disableContainer } from 'src/api/shipmentTracking';
import { CT_POLLING_FREQUENCY } from 'src/globals';
import { getDuration } from 'src/utils/utils';
import JavascriptTimeAgo from 'javascript-time-ago';
import en from 'javascript-time-ago/locale/en';
import { ModesOfService } from 'src/Library/Constants';
import { fetchTrackingData } from 'src/actions/shipmentExecutionActions';

const noDocumentsImg = require('src/assets/images/noDocuments.png');

let pollingAgent: any = null;

interface ContainerProps extends StylesProps<typeof commonStyles> {
  userData: UserData;
  selectedShipment: GetExecutionData;
  validContainerTypes: Record<string, string>;
  loadTracking: boolean;
  isAwb: boolean;
  bulkSelectedContainers: string[];
  setisAnyShipmentInTransitState: (stateVal: any) => void;
  setBulkSelectedContainers: (stateVal: any) => void;
}

const ContainerTracking = (props: ContainerProps) => {
  const { classes, selectedShipment, validContainerTypes, loadTracking, isAwb, setisAnyShipmentInTransitState } = props;
  const [loading, setLoading] = React.useState(true);
  const [blContainers, setBLContainers] = React.useState<SEBLCardData[]>([]);
  const [containers, setContainers] = React.useState<SEContainerCardData[]>([]);
  const [airCargos, setAirCargos] = React.useState<any[]>([]);
  const [selectedContainer, setSelectedContainer] = React.useState<any>();

  const stopTrackingPolling = () => {
    if (pollingAgent) {
      clearTimeout(pollingAgent);
      pollingAgent = undefined;
    }
  };

  const addToBulkSelectedContainers = (container: SEContainerCardData) => {
    props.setBulkSelectedContainers([...(props.bulkSelectedContainers || []), container?.id]);
  }

  const removeFromBulkSelectedContainers = (container: SEContainerCardData) => {
    props.setBulkSelectedContainers(props.bulkSelectedContainers?.filter((item) => item !== container?.id));
  }

  const pollTrackingApi = async (executionNumber: string, refresh = false) => {
    const currentData = containers;
    if (
      !refresh &&
      currentData &&
      currentData.filter((elem) => !elem.isTrackingPresent).length === 0 &&
      currentData.filter(
        (elem) =>
          elem.disableRefreshRequest && (getDuration(elem && elem.disableRefreshRequest) || -1) < 5
      ).length === 0
    ) {
      stopTrackingPolling();
      return;
    }
    loadData(false);
    stopTrackingPolling();
    startTrackingPoller(executionNumber);
  };

  const startTrackingPoller = (body, refresh = false) => {
    if (!pollingAgent) {
      pollingAgent = setTimeout(
        () => {
          pollTrackingApi(body, refresh);
        },
        refresh ? 5000 : CT_POLLING_FREQUENCY
      );
    }
  };

  const loadOceanShipmentData = async () => {
    const trackingNumber = selectedShipment.referenceNumber;

    const responseContainer = await fetchAllContainersForShipment(trackingNumber);
    const responseBL = await fetchAllBLsForShipment(trackingNumber);
    
    const isAnyShipmentInTransitState = responseContainer?.data?.some((shipment) => {
      return shipment?.status === 'IN_TRANSIT' || shipment?.status === 'TRANSSHIPMENT'
    })

    setisAnyShipmentInTransitState(!!isAnyShipmentInTransitState);
    
    setBLContainers(responseBL?.data || []);
    setContainers(responseContainer?.data || []);
  };

  const loadAirShipmentData = async () => {
    const executionNumber = selectedShipment.referenceNumber;
    const response = await fetchAllCargosForShipment(executionNumber);

    setAirCargos(response.data || []);
  };

  async function loadData(showLoader = true) {
    if (showLoader) {
      setLoading(true);
    }

    if (selectedShipment && [ModesOfService.AIR, ModesOfService.AIRCARGO].includes(selectedShipment.mode)) {
      await loadAirShipmentData();
    } else {
      await loadOceanShipmentData();
    }

    setLoading(false);
  }

  React.useEffect(() => {
    if (!selectedShipment.referenceNumber) {
      return;
    }
    loadData();
    JavascriptTimeAgo.locale(en);
  }, [selectedShipment.id]);

  React.useEffect(() => {
    if (loadTracking) {
      loadData();
    }
  }, [loadTracking]);

  const resetTrackingKeys = (list, idMap) => {
    list.forEach((item) => {
      if (idMap[item.id]) {
        item.isTrackingFailed = false;
        item.isTrackingPresent = false;
      }
    });
  };

  const refreshCard = (containerIdMap) => {
    const containerList = lodash.cloneDeep(containers);
    const blList = lodash.cloneDeep(blContainers);
    resetTrackingKeys(containerList, containerIdMap);
    resetTrackingKeys(blList, containerIdMap);
    setContainers(containerList);
    setBLContainers(blList);
  };

  const handleBulkRefresh = async (idMap, trackingType = 'CONTAINER') => {
    const executionNumber = selectedShipment.id;
    const shipmentIds = Object.keys(idMap);
    stopTrackingPolling();
    refreshCard(idMap);
    const response =
      trackingType === 'CONTAINER'
        ? await containerTrackingRefresh({ containerId: shipmentIds })
        : await blTrackingRefresh({ blId: shipmentIds });
    if (!response.isSuccess) {
      message.error(response.errorMessage);
    }
    startTrackingPoller(executionNumber, true);
  };

  const handleDeleteTracking = async (idMap, trackingType = 'CONTAINER') => {
    const executionNumber = selectedShipment.id;
    const shipmentIds = Object.keys(idMap);
    stopTrackingPolling();
    refreshCard(idMap);
    const response =
      trackingType === 'CONTAINER'
        ? await disableContainer(shipmentIds)
        : await disableBl(shipmentIds);
    if (!response.isSuccess) {
      message.error(response.errorMessage);
    }
    await fetchTrackingData(executionNumber)
    
    startTrackingPoller(executionNumber, true);
  }

  const handleContainerReset = async (idMap) => {
    const executionNumber = selectedShipment.id;
    const shipmentIds = Object.keys(idMap);
    stopTrackingPolling();
    const response = await containerTrackingReset({
      containerId: shipmentIds[0]
    });
    if (!response.isSuccess) {
      message.error(response.errorMessage);
    } else {
      loadData();
    }
    startTrackingPoller(executionNumber, true); 
  }

  const getSectionData = (sectionType: keyof typeof SHIPMENT_TYPE) => {
    switch (sectionType) {
      case SHIPMENT_TYPE.HBL:
      case SHIPMENT_TYPE.BL:
        // console.log('blContainers', blContainers);
        return {
          title: 'BL/HBL/Booking',
          isCardListEmpty: Boolean(!blContainers.length),
          cardList: blContainers,
          cardType: sectionType,
        };

      case SHIPMENT_TYPE.AWB:
        return {
          title: 'Air Cargo',
          isCardListEmpty: Boolean(!airCargos?.length),
          cardList: airCargos,
          cardType: SHIPMENT_TYPE.AWB,
        };

      default:
        return {
          title: 'Containers',
          isCardListEmpty: Boolean(!containers?.length),
          cardList: containers,
        };
    }
  };

  function getCardType(type: keyof typeof SHIPMENT_TYPE) {
    if (!type) {
      return 'Container';
    }
    if (type === SHIPMENT_TYPE.HBL) {
      return SHIPMENT_TYPE.HBL;
    }
    return SHIPMENT_TYPE.BL;
  }

  const renderCardList = (sectionType: keyof typeof SHIPMENT_TYPE) => {
    const { isCardListEmpty, title, cardList, cardType } = getSectionData(sectionType);
    if (loading || isCardListEmpty) {
      return null;
    }

    return (
      <div style={{ marginBottom: 16 }}>
        <div className={classes.title}>{title}</div>
        {(cardList as any[]).map((item) => (
          <ContainerCard
            cardData={item}
            cardType={cardType || getCardType(item.shipmentType)}
            validContainerTypes={validContainerTypes}
            handleOpenTracking={() => {
              setSelectedContainer(item);
            }}
            refreshCard={() => item && handleBulkRefresh({ [item.id]: item }, sectionType)}
            isAwb={isAwb}
            resetContainer={() => item && handleContainerReset({ [item.id]: item })}
            deleteCard={() => item && handleDeleteTracking({ [item.id]: item }, sectionType)}
            addToBulkSelectedContainers={addToBulkSelectedContainers}
            removeFromBulkSelectedContainers={removeFromBulkSelectedContainers}
          />
        ))}
      </div>
    );
  };

  const renderSideDrawer = () => {
    const isBL = selectedContainer?.shipmentType === SHIPMENT_TYPE.BL;
    const isBK = selectedContainer?.shipmentType === SHIPMENT_TYPE.BK;
    const isHBL = selectedContainer?.shipmentType === SHIPMENT_TYPE.HBL;

    return (
      !!selectedContainer &&
      !isAwb && (
        <SideDrawerTracking
          currentEventIndex={selectedContainer.currentEventIndex}
          tracking={selectedContainer.tracking}
          containerNumber={isBL ? '' : selectedContainer?.containerNumber}
          lastSuccessfulTrackingStatusUpdatedAt={
            selectedContainer.lastSuccessfulTrackingStatusUpdatedAt
          }
          blNumber={isBL ? selectedContainer.shipmentNumber : selectedContainer.blNumber}
          hblNumber={isHBL ? selectedContainer.shipmentNumber : selectedContainer.hblNumber}
          bookingNumber={selectedContainer.bookingNumber}
          visible={!!selectedContainer}
          onClose={() => setSelectedContainer(undefined)}
          isBL={isBL}
          isBK={isBK}
          isHBL={isHBL}
          shipmentNumber={getShipmentNumber(selectedContainer)}
        />
      )
    );
  };

  const getShipmentNumber = (cardData) => {
    const { blNumber, hblNumber, bookingNumber, shipmentNumber, containerNumber, linkedBlNumber } = cardData;

    switch (cardData.shipmentType) {
      case SHIPMENT_TYPE.BK:
        return `(BK# ${bookingNumber || shipmentNumber})`;
      case SHIPMENT_TYPE.BL:
        return `(BL# ${blNumber || shipmentNumber})`;
      case SHIPMENT_TYPE.CN:
        return `(CN# ${containerNumber || shipmentNumber})`;
      case SHIPMENT_TYPE.HBL:
        return `(HBL# ${hblNumber || shipmentNumber})`;
      default: {
        if (containerNumber) return `(CN# ${containerNumber || shipmentNumber})`;
        if (blNumber) return `(BL# ${blNumber || shipmentNumber})`;
        if (bookingNumber) return `(BK# ${bookingNumber || shipmentNumber})`;
        if (hblNumber) return `(HBL# ${hblNumber || shipmentNumber})`;
        return null;
      }
    }
  };

  const renderContent = () => {
    if (loading) {
      return <Loader style={{  marginLeft: '0px', marginTop: '25px' }} zIndex={1} />;
    }
    if (!blContainers?.length && !containers?.length && !airCargos?.length) {
      return (
        <div className={classes.noDocument}>
          <img src={noDocumentsImg} style={{ width: 400 }}></img>
          <div style={{ fontSize: 24 }}>Tracking Not Available</div>
        </div>
      );
    }
    return (
      <React.Fragment>
        {renderCardList('BL')}
        {renderCardList('CONTAINER')}
        {renderCardList('AWB')}
        {renderSideDrawer()}
      </React.Fragment>
    );
  };

  return <div className={classes.main}>{renderContent()}</div>;
};

const mapStateToProps = ({ masterData, shipmentExecutionReducer }) => {
  return {
    validContainerTypes: masterData.execution.validContainerTypes,
    loadTracking: shipmentExecutionReducer.loadTracking,
  };
};

const options: HocOptions = {
  connectJss: {
    useJss: true,
    styleSheet: commonStyles,
  },
  connectRedux: {
    useRedux: true,
    mapStateToProps,
  },
};

const Container = GenericHoc(options)(ContainerTracking);
export default Container;
